<template>
  <div class="multi-banner-container" v-if="banners.length">
    <swiper
      :options="sliderOptions"
      ref="multiBannerSwiperRef"
      @slideChange="slideChanged"
      @ready="onReady"
      @click.native.capture="onSwiperClick"
    >
      <swiper-slide
        v-for="(b, index) in banners"
        :key="getBannerKey(b, index)"
        :class="getBannerKey(b, index)"
      >
        <div v-if="b.isDynamicBanner">
          <div :style="getStyleDynamicDescription(b)" class="banner-dynamic-content">
            {{ b.description }}
          </div>
          <img
            v-for="subImage in b.subImages"
            :key="subImage.id"
            :src="subImage.subImageUrl"
            :style="getStyleDynamicSubImage(subImage)"
            class="banner-dynamic-content"
            :alt="`pm-aktif-logo-${subImage.id}`"
          />
        </div>
        <BrandMediaContentBox
          ref="multiBannerMediaBoxes"
          :mediaSrc="b.image"
          :href="b.link"
          :bannerItem="b"
          :isSliderClick="true"
          :lazyload="false"
          :hideOnError="true"
          @onClickSliderContent="onClickSliderContent"
        />
      </swiper-slide>
      <div class="swiper-button-prev" id="prev-element" slot="button-prev" />
      <div class="swiper-button-next" id="next-element" slot="button-next" />
      <div class="swiper-pagination" slot="pagination" />
    </swiper>
  </div>
</template>

<script>
import BrandMediaContentBox from '@/components/brand/BrandMediaContentBox/BrandMediaContentBox.vue';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import 'swiper/dist/css/swiper.css';
import { Common } from '@/services/Api/index';
import {
  ACTION_TYPE,
  JOURNEY_ITEM_TYPE,
  STORAGE_ACTION_TYPE,
} from '@/constants/journey.constants.js';
import { logJourneyAction } from '@/utils/journeyActionLogHelper';
import { BANNER_TYPES } from '@/constants/banner.constants.js';
import { setBannerSliderOptions } from '@/utils/bannerUtils';
import StorageProps from '@/mixins/storageProps.js';

export default {
  name: 'MultiBannerSlider',
  components: {
    swiper,
    swiperSlide,
    BrandMediaContentBox,
  },
  mixins: [StorageProps],
  data() {
    return {
      banners: [],
      position: 0,
      sliderOptions: {
        loop: true,
        slidesPerView: 4,
        spaceBetween: 20,
        threshold: 5,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
      },
      scrolled: 0,
      isSliderVisible: false,
      sliderObserver: null,
      isInitialView: true,
      lastIndex: null,
    };
  },
  mounted() {
    Common.getMultiBannerData().then(res => {
      if (res.data && res.data.Data) {
        const dynamicBanners = res.data.Data.dynamicBanners.map(banner => ({
          ...banner,
          image: banner.bannerImage,
          isDynamicBanner: true,
          bannerId: banner.id || banner.bannerId,
        }));

        const regularBanners = res.data.Data.banners.map(banner => ({
          ...banner,
          image: banner.image,
          isDynamicBanner: false,
          bannerId: banner.id || banner.bannerId,
        }));

        const combinedBanners = [...regularBanners, ...dynamicBanners];

        combinedBanners.sort((a, b) => a.order - b.order);

        this.banners = combinedBanners.map((banner, index) => ({
          ...banner,
          position: index + 1,
        }));

        const config = res.data.Data?.config;
        const { sliderOptions, banners } = setBannerSliderOptions(
          config,
          this.sliderOptions,
          this.banners,
          false,
          BANNER_TYPES.MultiBanner,
        );

        this.sliderOptions = sliderOptions;
        this.banners = banners;
      }
    });
  },
  methods: {
    getBannerKey(banner, index) {
      return `multi-${banner.bannerId}-${index}`;
    },
    getStyleDynamicDescription(dynamicBanner) {
      return `top:${dynamicBanner.topDescription}px; left:${
        dynamicBanner.leftDescription
      }px; font-family:${dynamicBanner.fontFamily}; font-size:${
        dynamicBanner.fontSize
      }px; font-weight:${dynamicBanner.isBold ? 'bold' : 'none'}; color:${
        dynamicBanner.descriptionColor
      }; width:${dynamicBanner.widthDescription}px; height:${dynamicBanner.heightDescription}px;
      text-align:${dynamicBanner.textAlignDescription};`;
    },
    getStyleDynamicSubImage(subImage) {
      return `top:${subImage.topSubImage}px; left:${subImage.leftSubImage}px; width: ${subImage.widthSubImage}px; height: ${subImage.heightSubImage}px;`;
    },
    onReady() {
      this.$nextTick(() => {
        this.initSliderObserver();
      });
    },
    initSliderObserver() {
      if (this.sliderObserver) {
        return;
      }

      this.sliderObserver = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          this.isSliderVisible = entry.isIntersecting;
          this.syncSliderAutoplay();

          if (!entry.isIntersecting) {
            return;
          }
          // track visible banners on initial visibility and viewport re-entry
          this.trackVisibleBanners();
        },
        { threshold: 0.5 },
      );

      if (this.$el instanceof Element) {
        this.sliderObserver.observe(this.$el);
      }
    },
    syncSliderAutoplay() {
      const swiper = this.$refs.multiBannerSwiperRef?.swiper;

      if (!swiper?.autoplay) {
        return;
      }

      const isAutoplayConfigured = Boolean(this.sliderOptions?.autoplay);

      if (!isAutoplayConfigured) {
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        }
        return;
      }

      if (this.isSliderVisible) {
        swiper.autoplay.start();
      } else {
        swiper.autoplay.stop();
      }
    },
    logJourneyActionHandler(ids, actionType, storageActionType) {
      logJourneyAction(
        this.customerCode,
        ids,
        JOURNEY_ITEM_TYPE.SUB_BANNER,
        actionType,
        storageActionType,
      );
    },
    onSwiperClick(event) {
      const swiper = this.$refs.multiBannerSwiperRef?.swiper;

      if (!swiper || !this.banners.length) {
        return;
      }

      // swiper sets allowClick to false for drag/swipe events
      if (!swiper.allowClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const clickedSlide = event.target.closest('.swiper-slide');
      // ignore navigation clicks
      if (!clickedSlide) {
        return;
      }

      const swiperSlideIndex = clickedSlide.getAttribute('data-swiper-slide-index');

      if (swiperSlideIndex === null) {
        return;
      }

      const bannerIndex = Number(swiperSlideIndex);
      const banner = this.banners[bannerIndex];

      if (!banner) {
        return;
      }

      const mediaBoxes = this.$refs.multiBannerMediaBoxes;
      const mediaBox = Array.isArray(mediaBoxes) ? mediaBoxes[bannerIndex] : mediaBoxes;

      if (!mediaBox) {
        return;
      }

      if (!mediaBox?.goToLink) {
        return;
      }

      // prevents BrandMediaContentBox @click to be triggered again
      event.stopPropagation();

      mediaBox?.goToLink();
    },
    onClickSliderContent(bannerItem) {
      this.$emit('onClickSliderContent', { ...bannerItem });

      if (bannerItem.isDynamicBanner) {
        this.logJourneyActionHandler(
          [bannerItem.bannerId],
          ACTION_TYPE.CLICK,
          STORAGE_ACTION_TYPE.CLICK,
        );
      }
    },
    slideChanged() {
      if (!this.isSliderVisible) {
        return;
      }

      this.trackVisibleBanners();
    },
    getActiveBanners() {
      const swiper = this.$refs.multiBannerSwiperRef?.swiper;

      if (!swiper || !this.banners.length) {
        return [];
      }

      const index = swiper.realIndex;
      const visibleBannerCount = Math.min(4, this.banners.length);
      const activeBanners = [];

      for (let i = 0; i < visibleBannerCount; i++) {
        const bannerIndex = (index + i) % this.banners.length;
        activeBanners.push(this.banners[bannerIndex]);
      }

      return activeBanners;
    },
    trackVisibleBanners() {
      const swiper = this.$refs.multiBannerSwiperRef?.swiper;

      if (!swiper) {
        return;
      }

      const index = swiper.realIndex;
      if (this.lastIndex === index) {
        return;
      }
      this.lastIndex = index;
      const activeBanners = this.getActiveBanners();

      if (!activeBanners.length) {
        return;
      }

      const activeDynamicBanners = activeBanners.filter(banner => banner.isDynamicBanner);

      if (activeDynamicBanners.length > 0) {
        this.logJourneyActionHandler(
          activeDynamicBanners.map(banner => banner.bannerId),
          ACTION_TYPE.VIEW,
          STORAGE_ACTION_TYPE.VIEW,
        );
      }

      if (this.isInitialView) {
        this.$emit('initSubSlider', activeBanners);
        this.isInitialView = false;
        return;
      }

      activeBanners.forEach(banner => {
        this.$emit('onSubSliderChanged', banner);
      });
    },
  },
  beforeDestroy() {
    if (this.sliderObserver) {
      this.sliderObserver.disconnect();
      this.sliderObserver = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.multi-banner-container {
  position: relative;
  /* background-color: #f7f8fa; */
  padding-top: 60px;
  /* padding-bottom: 75px; */
  /deep/.swiper-wrapper {
    height: 350px;
    .swiper-slide {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      .clickable {
        cursor: pointer;
      }
      .bottom-text {
        position: absolute;
        height: 80px;
        bottom: 0;
        left: 0;
        right: 0;
        color: #fff;
        font-size: 14px;
        padding: 42px 16px 22px;
        line-height: 16px;
        background: linear-gradient(0deg, rgba(35, 48, 61, 0.6) 0%, rgba(35, 48, 61, 0) 100%);
      }
    }
  }
  .swiper-button-next,
  .swiper-button-prev {
    opacity: 0;
    position: absolute;
    width: 67px;
    height: 100%;
    background-size: cover;
    z-index: 2;
    top: 23px;
    -webkit-transition: all 0.2s ease-in-out;
    transition: all 0.2s ease-in-out;
  }
  .swiper-button-next {
    right: 0;
    background-image: url('~@/assets/bg/next-shape.png');
  }
  .swiper-button-prev {
    left: 0;
    background-image: url('~@/assets/bg/prev-shape.png');
  }
  &:hover {
    .swiper-button-prev.swiper-button-disabled,
    .swiper-button-next.swiper-button-disabled {
      opacity: 0.3;
      pointer-events: auto;
      display: none;
    }
    .swiper-button-prev,
    .swiper-button-next {
      opacity: 1;
    }
  }

  .swiper-pagination {
    line-height: 8px;
    left: 50%;
    width: 50%;
    transform: translateX(-50%);
  }

  /deep/ .swiper-pagination-bullet {
    background-color: #fff;
    opacity: 0.6;
    border: 1px solid #ffffff;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    &-active {
      opacity: 1;
    }
  }
}
.banner-dynamic-content {
  position: absolute;
  z-index: 10;
  pointer-events: none;
}
</style>
